// ============================================================
// SkyPlus Tenant & Sector Models
// app/components/skyplus/shared/sector/sector.model.ts
//
// A deployment is defined by three orthogonal axes:
//   1. TenantMode - who is this deployment for (client / dev / demo)?
//   2. FeatureFlags - which capabilities are enabled for this deployment?
//   3. Pack - which vertical vocabulary + rules are active?
//
// All three are controlled by the tenant config file loaded from the
// backend. The frontend reads them through SectorService and adapts.
// ============================================================

import { AppConfigService } from '@alfresco/adf-core';

/**
 * TenantMode - controls visibility of admin/demo UI affordances.
 *
 *   client   - deployed at a paying client site. Administrative and
 *              demo-only UI is invisible. Default for production.
 *   dev      - running on a developer laptop. Admin UI visible for
 *              testing. Same data safety as client.
 *   demo     - running on a presentation instance. Admin UI visible,
 *              pack switching enabled, seed data available.
 *
 * This is separate from feature flags. A client can have AI chat
 * enabled and still be in client mode - they get the feature,
 * they just don't get the pack switcher or the demo affordances.
 */
export type TenantMode = 'client' | 'dev' | 'demo';

/**
 * FeatureFlags - every enhanced feature is gated behind a flag.
 * Default for all flags is false. Adding a new flag means clients
 * who haven't bought the feature get NOTHING new in their UI -
 * the existing app behaves exactly as it did before.
 *
 * Rules for adding a new flag:
 *   1. Add the field here, default false
 *   2. Gate the UI behind sector.feature('flagName')
 *   3. Gate the route behind the FeatureRouteGuard
 *   4. Gate the menu row behind the same flag at the backend
 *   5. Never remove a flag - only add. Old configs stay valid.
 */
export interface FeatureFlags {
  aiChat:          boolean;
  flipViewer:      boolean;
  piiDetection:    boolean;
  autoCaseFile:    boolean;
  emailIngest:     boolean;
  collaborativeAnnotate: boolean;
  documentCompare: boolean;
  advancedDashboards: boolean;
}

/**
 * Pack - a JSON bundle representing a vertical.
 * See prior comments for the full purpose. Unchanged from Phase 1.
 */
export interface Pack {
  packId:       string;
  packName:     string;
  packVersion:  string;
  accentColor:  string;

  vocabulary:     VocabularyMap;
  caseFileTypes:  CaseFileType[];
  integrations:   Integration[];
  aiPrompts:      AiPrompts;
  compliance:     Compliance;
  dashboards:     DashboardLink[];
}

export type VocabularyMap = Record<string, string>;

export const VOCAB_KEYS = [
  'caseFile.singular',
  'caseFile.plural',
  'caseFile.new',
  'participant.singular',
  'participant.plural',
  'participant.id',
  'paymentPack',
  'masterRecord',
  'masterRecord.id',
  'masterRecord.singular',
  'masterRecord.plural',
  'dashboard.title',
  'ai.placeholder',
  'upload.target'
] as const;

export type VocabKey = typeof VOCAB_KEYS[number];

export interface CaseFileType {
  key:            string;
  label:          string;
  aspects:        string[];
  classification: 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'RESTRICTED';
  workflow:       string | null;
  checklist:      ChecklistItem[];
}

export interface ChecklistItem {
  docType:  string;
  required: boolean;
  trigger:  string;
}

export interface Integration {
  key:     string;
  label:   string;
  trigger: string;
  action:  string;
}

export interface AiPrompts {
  systemContext:    string;
  suggestedQueries: string[];
  entityHints:      string[];
}

export interface Compliance {
  regulators:     string[];
  retentionRules: RetentionRule[];
  piiFields:      string[];
}

export interface RetentionRule {
  docType:     string;
  years:       number;
  afterEvent?: string;
}

export interface DashboardLink {
  key:   string;
  route: string;
  title: string;
}

export interface Brand {
  productName?:   string;
  primaryColor?:  string;
  primaryDark?:   string;
  primaryLight?:  string;
  accentColor?:   string;
  logoUrl?:       string;
  logoWhiteUrl?:  string;
  faviconUrl?:    string;
}

/**
 * TenantResponse - what GET /api/tenant/config returns.
 * Combines tenant mode, features, and the active pack in one
 * response so we make exactly one HTTP call at application boot.
 */
export interface TenantResponse {
  tenantId:       string;
  tenantName:     string;
  tenantMode:     TenantMode;
  brand?:         Brand;  
  features:       FeatureFlags;
  pack:           Pack;
  availablePacks: PackSummary[];
  loadedAt:       string;
}

export interface PackSummary {
  packId:      string;
  packName:    string;
  accentColor: string;
  enabled:     boolean;
}

/**
 * DEFAULT_FEATURES - the master list of flags, all disabled.
 * Used by the frontend fallback config when the backend is unreachable.
 * Also the canonical list - any flag not listed here does not exist.
 */
export const DEFAULT_FEATURES: FeatureFlags = {
  aiChat:                false,
  flipViewer:            false,
  piiDetection:          false,
  autoCaseFile:          false,
  emailIngest:           false,
  collaborativeAnnotate: false,
  documentCompare:       false,
  advancedDashboards:    false
};

/**
 * DEFAULT_BRAND - NEUTRAL vendor fallback ONLY. Used when the backend sends no
 * brand (tenant endpoint down / not yet provisioned). The REAL per-client brand
 * (colours, logo, product name) ALWAYS comes from the active profile
 * (skyplus.tenant.brand) via GET /api/tenant. Never hardcode a client brand
 * here — set it in that site's application.yml. Mirrors
 * SkyPlusLoginComponent.getDefaultBrand() so pre-auth and post-auth match.
 */
export const DEFAULT_BRAND: Brand = {
  productName:  'Document Management',
  primaryColor: '#2c3e50',
  primaryDark:  '#1a252f',
  primaryLight: '#3d566e',
  accentColor:  '#2980b9',
  logoUrl:      'assets/images/skyplus/logo.png',
  logoWhiteUrl: 'assets/images/skyplus/logo-white.png'
};

/**
 * configBrand - resolve the fallback brand from app.config.json's "brand"
 * block, merged over DEFAULT_BRAND so any missing field still resolves. Used
 * whenever the backend supplies no brand (endpoint down / not yet deployed /
 * no brand block) so a CLIENT deployment keeps its own theme + logo instead of
 * reverting to SkyPlus. Reads the same "brand" key the login screen already
 * uses, so pre-auth and post-auth stay consistent off one config block.
 */
export function configBrand(appConfig: AppConfigService): Brand {
  const b = appConfig.get<any>('brand', null);
  if (!b) { return { ...DEFAULT_BRAND }; }
  return {
    productName:  b.productName  || b.companyName || DEFAULT_BRAND.productName,
    primaryColor: b.primaryColor || DEFAULT_BRAND.primaryColor,
    primaryDark:  b.primaryDark  || DEFAULT_BRAND.primaryDark,
    primaryLight: b.primaryLight || DEFAULT_BRAND.primaryLight,
    accentColor:  b.accentColor  || DEFAULT_BRAND.accentColor,
    logoUrl:      b.logoUrl      || DEFAULT_BRAND.logoUrl,
    logoWhiteUrl: b.logoWhiteUrl || b.logoUrl || DEFAULT_BRAND.logoWhiteUrl,
    faviconUrl:   b.faviconUrl   || DEFAULT_BRAND.faviconUrl
  };
}